import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
    DEFAULT_FOTO_PERSPECTIVE,
    FotoPerspectiveConfig,
    getFotoPerspectiveConfig,
    isEmpty,
    setFotoPerspectiveConfig,
} from '../../../utils/functions'

const AjustesFoto = (): JSX.Element => {
    const user = useSelector((state: any) => state.user)
    const navigate = useNavigate()
    const [config, setConfig] = useState<FotoPerspectiveConfig>(getFotoPerspectiveConfig)
    const [saved, setSaved] = useState(false)

    useEffect(() => {
        if (isEmpty(user) || user.level !== 1) {
            navigate('/')
        }
    }, [user, navigate])

    const update = (partial: Partial<FotoPerspectiveConfig>) => {
        setConfig((prev) => ({ ...prev, ...partial }))
        setSaved(false)
    }

    const handleSave = () => {
        setFotoPerspectiveConfig(config)
        setSaved(true)
    }

    const handleReset = () => {
        setConfig({ ...DEFAULT_FOTO_PERSPECTIVE })
        setFotoPerspectiveConfig({ ...DEFAULT_FOTO_PERSPECTIVE })
        setSaved(true)
    }

    if (isEmpty(user) || user.level !== 1) {
        return <></>
    }

    const skew = config.enabled ? config.skew : 0
    const topInset = 18 * skew * 10

    return (
        <div className="flex flex-col items-center w-full min-h-[70vh] px-4 py-8 gap-6">
            <h1 className="text-2xl font-semibold text-violetDark">Ajustes de foto de cartas</h1>
            <p className="max-w-xl text-sm text-center text-zinc-600">
                La foto final sigue siendo rectangular: se agranda el fondo para que no se noten bordes cortados. Probá,
                guardá y hacé una foto en cualquier tirada.
            </p>

            <div className="flex flex-col w-full max-w-lg gap-5 p-6 bg-white border shadow-sm rounded-2xl border-zinc-200">
                <label className="flex items-center justify-between gap-3 text-sm font-medium">
                    <span>Aplicar perspectiva</span>
                    <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) => update({ enabled: e.target.checked })}
                        className="w-5 h-5 accent-violetDark"
                    />
                </label>

                <label className="flex flex-col gap-2 text-sm">
                    <span className="flex justify-between font-medium">
                        Inclinación (tilt)
                        <span className="text-zinc-500">{config.tilt.toFixed(2)}</span>
                    </span>
                    <input
                        type="range"
                        min={0}
                        max={0.35}
                        step={0.01}
                        value={config.tilt}
                        disabled={!config.enabled}
                        onChange={(e) => update({ tilt: Number(e.target.value) })}
                        className="w-full accent-violetDark"
                    />
                    <span className="text-xs text-zinc-500">Qué tan “desde arriba” se ve la mesa. Probá 0.08–0.18.</span>
                </label>

                <label className="flex flex-col gap-2 text-sm">
                    <span className="flex justify-between font-medium">
                        Apertura (skew)
                        <span className="text-zinc-500">{config.skew.toFixed(2)}</span>
                    </span>
                    <input
                        type="range"
                        min={0}
                        max={0.25}
                        step={0.01}
                        value={config.skew}
                        disabled={!config.enabled}
                        onChange={(e) => update({ skew: Number(e.target.value) })}
                        className="w-full accent-violetDark"
                    />
                    <span className="text-xs text-zinc-500">
                        Fuerza del achique hacia el fondo. Probá 0.05–0.12.
                    </span>
                </label>

                <label className="flex flex-col gap-2 text-sm">
                    <span className="flex justify-between font-medium">
                        Altura de cartas
                        <span className="text-zinc-500">{config.heightScale.toFixed(2)}</span>
                    </span>
                    <input
                        type="range"
                        min={0.7}
                        max={1}
                        step={0.01}
                        value={config.heightScale}
                        onChange={(e) => update({ heightScale: Number(e.target.value) })}
                        className="w-full accent-violetDark"
                    />
                    <span className="text-xs text-zinc-500">
                        Más bajo = cartas menos altas/estiradas. Probá 0.82–0.90.
                    </span>
                </label>

                <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium">Vista previa (foto rectangular)</span>
                    <svg viewBox="0 0 100 70" className="w-full h-40 bg-zinc-100 rounded-xl">
                        <rect x="2" y="2" width="96" height="66" fill="#e4e4e7" stroke="#a1a1aa" strokeWidth="1" />
                        <polygon
                            points={`${8 + topInset},12 ${92 - topInset},12 88,58 12,58`}
                            fill="#9400d3"
                            fillOpacity="0.28"
                            stroke="#9400d3"
                            strokeWidth="1.2"
                        />
                        <text x="50" y="38" textAnchor="middle" className="fill-violetDark text-[7px]">
                            cartas + fondo
                        </text>
                    </svg>
                </div>

                <div className="flex flex-wrap gap-3 pt-2">
                    <button
                        type="button"
                        onClick={handleSave}
                        className="px-4 py-2 text-sm text-white rounded-lg bg-violetDark hover:opacity-90"
                    >
                        Guardar
                    </button>
                    <button
                        type="button"
                        onClick={handleReset}
                        className="px-4 py-2 text-sm bg-white border rounded-lg border-zinc-300 hover:bg-zinc-50"
                    >
                        Restaurar defaults
                    </button>
                    {saved && <span className="self-center text-sm text-green-700">Guardado</span>}
                </div>
            </div>
        </div>
    )
}

export default AjustesFoto
